<template>
  <span>
    <v-dialog
      v-model="dialog"
      max-width="650"
      persistent
      scrollable
      :retain-focus="false"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn class="bg-primary-gradient primary" v-bind="attrs" v-on="on">
          <v-icon class="mr-3">{{ icons.lockReset }}</v-icon>
          Password Reset
        </v-btn>
      </template>

      <v-card>
        <v-card-title>
          <h4 class="mb-1">Reset User Password</h4>
        </v-card-title>

        <v-divider class="mb-5" />

        <!-- Step 1: confirmation -->
        <v-card-text v-if="!newPassword" class="my-3">
          <v-alert type="warning" outlined class="mb-4">
            <strong>Warning:</strong> This will replace the user's current
            password with a new one. They will need to use the new password to
            log into their account.
          </v-alert>

          <p class="body-1 mb-0">
            Are you sure you want to reset the password for this user?
          </p>
        </v-card-text>

        <!-- Step 2: reveal generated password (shown once) -->
        <v-card-text v-else class="my-3">
          <v-alert type="success" outlined class="mb-4">
            The password has been reset. This password is
            <strong>shown only once</strong> — copy it now and share it securely
            with the user.
          </v-alert>

          <v-text-field
            :value="newPassword"
            label="New password"
            readonly
            outlined
            flat
            hide-details
            class="mb-2"
          >
            <template v-slot:append-outer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    icon
                    @click="copyPassword"
                  >
                    <v-icon>{{ icons.copy }}</v-icon>
                  </v-btn>
                </template>
                <span>Copy to clipboard</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-card-text>

        <v-divider class="mb-3" />

        <v-card-actions class="px-5 pb-5">
          <v-spacer />

          <template v-if="!newPassword">
            <v-btn
              color="warning"
              :loading="isResetting"
              @click="closeDialog"
              text
            >
              Cancel
            </v-btn>

            <v-btn
              color="primary"
              :loading="isResetting"
              @click="confirmReset"
              text
            >
              Confirm Reset
            </v-btn>
          </template>

          <v-btn v-else color="primary" @click="closeDialog" text>
            Done
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-toast ref="toast" />
  </span>
</template>

<script>
import VToast from '@/components/elements/Toast'
import { mapActions, mapState } from 'vuex'
import { mdiLockReset, mdiContentCopy } from '@mdi/js'

export default {
  name: 'PasswordResetDialog',

  components: {
    VToast,
  },

  data() {
    return {
      icons: {
        lockReset: mdiLockReset,
        copy: mdiContentCopy,
      },
      dialog: false,
      isResetting: false,
      newPassword: '',
    }
  },

  computed: {
    ...mapState({
      user: (state) => state.users.selectedUser,
    }),
  },

  methods: {
    ...mapActions({
      resetUserPassword: 'users/resetUserPassword',
    }),

    closeDialog() {
      this.dialog = false
      // Discard the password so it cannot be revealed again.
      this.newPassword = ''
    },

    async copyPassword() {
      let copied = false

      // Prefer the async Clipboard API (works inside dialogs / focus traps,
      // available on localhost and https). Fall back to v-clipboard's
      // execCommand-based copy for older / non-secure contexts.
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.newPassword)
          copied = true
        } else {
          copied = this.$clipboard(this.newPassword)
        }
      } catch (err) {
        copied = this.$clipboard(this.newPassword)
      }

      this.$refs.toast.open({
        color: copied ? 'success' : 'error',
        message: copied
          ? 'Password copied to clipboard.'
          : 'Could not copy automatically. Please copy the password manually.',
      })
    },

    async confirmReset() {
      this.isResetting = true

      try {
        const data = await this.resetUserPassword(this.user.id)
        this.newPassword = data.password
      } catch (err) {
        const message =
          err.response?.data?.message ||
          'Failed to reset password. Please try again.'
        this.$refs.toast.open({
          color: 'error',
          message: message,
        })
      } finally {
        this.isResetting = false
      }
    },
  },
}
</script>
